import React, { memo, useEffect, useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { StepItem } from '../../types';

interface StatusStepsProps {
    steps: StepItem[];
    startedAt?: number;   // 计时起点（首步骤创建时刻）
    doneAt?: number;      // 结束时刻；undefined = 仍在进行，计时器持续走
    onCancel?: () => void;   // 2026-09-17 友好性③：取消按钮（进行中才显示）
}

function fmtElapsed(ms: number): string {
    const s = Math.max(0, Math.floor(ms / 1000));
    return String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
}

// 四色：active 蓝 / done 绿 / warning 橙 / error 红（对齐离线版状态框配色语义）
const STATUS_META: Record<StepItem['status'], { color: string; bg: string; Icon: typeof Loader2 }> = {
    active:  { color: '#2563eb', bg: '#eff6ff', Icon: Loader2 },
    done:    { color: '#16a34a', bg: '#f0fdf4', Icon: CheckCircle2 },
    warning: { color: '#d97706', bg: '#fffbeb', Icon: AlertTriangle },
    error:   { color: '#dc2626', bg: '#fef2f2', Icon: XCircle },
};

const StatusSteps: React.FC<StatusStepsProps> = memo(({ steps, startedAt, doneAt, onCancel }) => {
    const [expandedSeqs, setExpandedSeqs] = useState<Set<number>>(new Set());
    const [collapsed, setCollapsed] = useState(false); // 整个步骤框折叠
    const [hintDismissed, setHintDismissed] = useState(false); // 2026-09-22 友好性：长耗时提醒条可关
    const [, forceTick] = useState(0);

    const live = !doneAt;
    // 0922c：Phase 1 收尾冻结 doneAt 后 Phase 2 步骤仍 active——tick 也要跟末步 active 走，
    // 否则提醒条/计时在真正漫长的阶段停摆
    const lastActive = steps.length > 0 && steps[steps.length - 1].status === 'active';
    const ticking = live || lastActive;
    // 进行中每 1s 重渲染刷新计时；结束后定格
    useEffect(() => {
        if (!ticking) return;
        const t = setInterval(() => forceTick(n => n + 1), 1000);
        return () => clearInterval(t);
    }, [ticking]);

    // 进行中每 1s 重渲染刷新计时；结束后定格
    // 注意：不能包 useMemo——forceTick 触发重渲染时 memo 依赖未变会返回缓存值，计时就不跳了
    const end = doneAt ?? Date.now();
    const start = startedAt ?? end;
    const elapsedMs = Math.max(0, end - start);

    const lastStatus = steps.length ? steps[steps.length - 1].status : 'active';
    // 2026-09-18：末步为取消行 → 头部显示"已取消"（终态回放/实时取消同口径）
    const lastText = steps.length ? steps[steps.length - 1].text : '';
    const isCancelled = /已取消/.test(lastText);
    const headIcon = isCancelled ? '⏹' : lastStatus === 'error' ? '❌' : lastStatus === 'warning' ? '⚠️' : live ? '⏳' : '✅';
    const headText = ticking ? '分析中' : isCancelled ? '已取消' : lastStatus === 'error' ? '已失败' : lastStatus === 'warning' ? '已完成（需关注）' : '已完成';

    const toggleStep = useCallback((seq: number) => {
        setExpandedSeqs(prev => {
            const next = new Set(prev);
            if (next.has(seq)) next.delete(seq); else next.add(seq);
            return next;
        });
    }, []);

    if (!steps.length) return null;

    return (
        <div className="status-steps">
            <div className="ss-head" onClick={() => setCollapsed(c => !c)} title={collapsed ? '展开步骤详情' : '折叠步骤详情'}>
                {live && onCancel && (
                    <button
                        className="ss-cancel-btn"
                        style={{ marginLeft: 'auto', marginRight: 8, fontSize: 12, padding: '1px 10px',
                                 border: '1px solid #d1d5db', borderRadius: 6, background: '#fff',
                                 color: '#6b7280', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); onCancel(); }}
                    >取消</button>
                )}
                <span className="ss-head-icon">{headIcon}</span>
                <span className={`ss-head-text ss-${lastStatus}`}>{headText}</span>
                <span className="ss-meta">
                    已用 <b>{fmtElapsed(elapsedMs)}</b> · 共 {steps.length} 步
                </span>
                <span className="ss-collapse-btn">{collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}</span>
            </div>

            {/* 2026-09-22 友好性：分析超过2分钟提醒可离开，结果保留在历史会话（用户裁决：短信通知需收集手机号，合规负担重，不取）
                0922c 修复：进行中判定不能只看 doneAt——Phase 1 收尾会冻结 doneAt，
                但 Phase 2（OCR+提取，真正漫长的阶段）步骤仍在追加；改看末步是否 active，
                且耗时用实时钟（doneAt 冻结后 elapsedMs 会定格，对 Phase 2 失真） */}
            {(() => {
                const lastLive = steps.length > 0 && steps[steps.length - 1].status === 'active' && !isCancelled;
                const inFlight = live || lastLive;
                const elapsedReal = Math.max(0, Date.now() - start);
                return inFlight && !hintDismissed && elapsedReal >= 120000 && (
                    <div className="ss-hint">
                        <span>☕ 这份样本要分析一会儿，可以先去忙别的——结果会自动保留在左侧历史会话里，过几分钟回来查看即可。</span>
                        <button className="ss-hint-close" onClick={() => setHintDismissed(true)} title="不再提示">×</button>
                    </div>
                );
            })()}

            {!collapsed && (
                <div className="ss-list">
                    {steps.map(s => {
                        const meta = STATUS_META[s.status] || STATUS_META.active;
                        const expandable = !!s.detail && s.detail !== s.text;
                        const expanded = expandedSeqs.has(s.seq);
                        return (
                            <div key={s.seq} className="ss-item">
                                <div
                                    className={`ss-row ${expandable ? 'ss-row-expandable' : ''}`}
                                    onClick={() => expandable && toggleStep(s.seq)}
                                >
                                    <span className="ss-seq" style={{ color: meta.color }}>{s.seq}.</span>
                                    <span className="ss-dot" style={{ background: meta.color }}>
                                        {s.status === 'active' && <span className="ss-pulse" style={{ borderColor: meta.color }} />}
                                    </span>
                                    {expandable
                                        ? <span className="ss-chevron">{expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
                                        : <span className="ss-chevron-placeholder" />}
                                    <span className="ss-text" style={{ color: s.status === 'active' ? '#1d4ed8' : '#374151' }}>{s.text}</span>
                                    {s.time && <span className="ss-time">{s.time}</span>}
                                </div>
                                {expandable && expanded && (
                                    <pre className="ss-detail" style={{ borderLeftColor: meta.color, background: meta.bg }}>{s.detail}</pre>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

StatusSteps.displayName = 'StatusSteps';

export default StatusSteps;
